/*
 * Keeps every header cart-count display in sync after any cart mutation.
 *
 * The header has multiple cart anchors:
 *   - Dawn's default `#cart-icon-bubbles` with `.cart-count-bubble`
 *     (hidden on desktop in this theme).
 *   - A custom ai-gen header block with `<span class="ai-cart-count-...">`
 *     (visible on desktop).
 *
 * Different add-to-cart flows publish or skip Dawn's PUB_SUB_EVENTS.cartUpdate.
 * We listen there *and* intercept fetch/XHR to /cart/(add|change|update|clear)
 * so the count refreshes regardless of caller.
 *
 * /cart.js is fetched with `cache: 'no-store'` + a cache-busting query to
 * avoid the stale-cart-after-add race that occasionally hit desktop.
 */
(function () {
  var inFlight = false;
  var lastAppliedCount = null;
  var cartEndpointRe = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  // Diagnostic surface — type `__smHdr` in the browser console.
  var dbg = (window.__smHdr = {
    version: '2026-05-13.5',
    loaded: true,
    subscribed: false,
    fetchPatched: false,
    xhrPatched: false,
    cartUpdates: 0,
    fetchHits: 0,
    xhrHits: 0,
    refreshes: 0,
    lastCount: null,
    targets: 0,
    log: []
  });
  function note(msg) { dbg.log.push(Date.now() + ' ' + msg); if (dbg.log.length > 50) dbg.log.shift(); }

  function updateDawnBubble(itemCount) {
    var anchor = document.getElementById('cart-icon-bubbles');
    if (!anchor) return 0;
    var bubble = anchor.querySelector('.cart-count-bubble');
    if (itemCount > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        anchor.appendChild(bubble);
      }
      bubble.textContent = '';
      if (itemCount < 100) {
        var visibleSpan = document.createElement('span');
        visibleSpan.setAttribute('aria-hidden', 'true');
        visibleSpan.textContent = itemCount;
        bubble.appendChild(visibleSpan);
      }
      var srSpan = document.createElement('span');
      srSpan.className = 'visually-hidden';
      srSpan.textContent = itemCount + ' items';
      bubble.appendChild(srSpan);
    } else if (bubble) {
      bubble.remove();
    }
    return 1;
  }

  function updateAiHeaderCount(itemCount) {
    // The ai_gen header block wraps the count span in `{% if cart.item_count > 0 %}`,
    // so it does not exist in DOM when cart is empty. Two passes:
    //   1. Update any existing spans.
    //   2. If count > 0 but no span exists yet, inject one into the cart
    //      anchor with the matching `ai-cart-count-<id>` class so existing
    //      CSS picks it up.
    var existing = document.querySelectorAll('[class*="ai-cart-count-"]');
    var hits = 0;

    existing.forEach(function (span) {
      if (itemCount > 0) {
        span.textContent = itemCount;
        span.style.display = '';
      } else {
        span.remove();
      }
      hits++;
    });

    if (itemCount > 0 && existing.length === 0) {
      var anchors = document.querySelectorAll('a[href$="/cart"][class*="ai-header-icon-"]');
      anchors.forEach(function (anchor) {
        var match = /ai-header-icon-([a-z0-9]+)/.exec(anchor.className);
        if (!match) return;
        var span = document.createElement('span');
        span.className = 'ai-cart-count-' + match[1];
        span.textContent = itemCount;
        anchor.appendChild(span);
        hits++;
      });
    }

    return hits;
  }

  function applyCount(itemCount) {
    lastAppliedCount = itemCount;
    dbg.lastCount = itemCount;
    var hits = updateDawnBubble(itemCount) + updateAiHeaderCount(itemCount);
    dbg.targets = hits;
    note('applyCount ' + itemCount + ' (targets=' + hits + ')');
  }

  function fetchCart() {
    return fetch('/cart.js?ts=' + Date.now(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function refreshFromServer() {
    if (inFlight) return;
    inFlight = true;
    dbg.refreshes++;
    note('refresh #' + dbg.refreshes);
    var prevCount = lastAppliedCount;
    fetchCart()
      .then(function (cart) {
        applyCount(cart.item_count);
        // Safety retry once after 600ms if the count didn't move — covers
        // the rare race where /cart.js fires before the server commits.
        if (prevCount !== null && cart.item_count === prevCount) {
          setTimeout(function () {
            fetchCart()
              .then(function (c2) { applyCount(c2.item_count); })
              .catch(function () { /* swallow */ });
          }, 600);
        }
      })
      .catch(function () { /* swallow */ })
      .finally(function () { inFlight = false; });
  }

  function handleCartUpdate(event) {
    dbg.cartUpdates++;
    var src = event && event.source ? event.source : 'unknown';
    var ic = event && event.cartData && typeof event.cartData.item_count === 'number' ? event.cartData.item_count : 'no-item_count';
    note('cartUpdate src=' + src + ' ic=' + ic);
    if (event && event.cartData && typeof event.cartData.item_count === 'number') {
      applyCount(event.cartData.item_count);
    } else {
      refreshFromServer();
    }
  }

  function subscribeWhenReady() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
      dbg.subscribed = true;
      note('subscribed');
    } else {
      setTimeout(subscribeWhenReady, 50);
    }
  }

  function installFetchInterceptor() {
    if (window.__smCartFetchPatched) return;
    window.__smCartFetchPatched = true;
    var originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var hit = cartEndpointRe.test(url);
      var resPromise = originalFetch.apply(this, arguments);
      if (hit) {
        dbg.fetchHits++;
        note('fetch hit ' + url);
        resPromise.then(function () { refreshFromServer(); }, function () { /* network err */ });
      }
      return resPromise;
    };
    dbg.fetchPatched = true;
  }

  function installXhrInterceptor() {
    if (window.__smCartXhrPatched) return;
    window.__smCartXhrPatched = true;
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__smUrl = url || '';
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var url = this.__smUrl;
      if (cartEndpointRe.test(url)) {
        dbg.xhrHits++;
        note('xhr hit ' + url);
        this.addEventListener('loadend', function () { refreshFromServer(); });
      }
      return origSend.apply(this, arguments);
    };
    dbg.xhrPatched = true;
  }

  function seedFromDom() {
    var span = document.querySelector('#cart-icon-bubbles .cart-count-bubble span[aria-hidden="true"]')
            || document.querySelector('[class*="ai-cart-count-"]');
    if (span) {
      var n = parseInt(span.textContent, 10);
      if (!isNaN(n)) {
        lastAppliedCount = n;
        dbg.lastCount = n;
        // The ai-cart-count span is server-rendered unconditionally, so
        // when cart is empty it shows "0" on first paint. Re-apply through
        // applyCount so empty carts hide the badge correctly.
        applyCount(n);
      }
    }
  }

  seedFromDom();
  subscribeWhenReady();
  installFetchInterceptor();
  installXhrInterceptor();
})();
